import { Pencil, RotateCcw, Send } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { PressReleaseModal } from '../components/PressReleaseModal';
import { pressReleasesApi } from '../pressReleaseApi';
import { pressReleaseLock, pressReleasePath, type PressReleaseItem } from '../pressReleaseTypes';

type Pending = { kind: 'delete' | 'restore' | 'publish'; record: PressReleaseItem };

/**
 * Public Relations › Press Release — the register.
 *
 * **Create opens the large form and lands on the new release** — a draft, always. Publishing is the
 * row's own entry and the profile banner's, under `press-releases.publish`; a published or deleted
 * release says why it cannot be edited rather than offering an Edit the server refuses.
 */
export function PressReleasesIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PressReleaseItem | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchReleases = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      pressReleasesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          releaseStatus: query.facets.releaseStatus?.join(','),
        },
        signal,
      ),
    [],
  );

  // The list row carries no document, so Edit reads the release whole before the form opens.
  const openEdit = useCallback(
    async (record: PressReleaseItem) => {
      try {
        setEditing(await pressReleasesApi.get(record.id));
      } catch (caught) {
        toast.failure(caught instanceof ApiError ? caught.message : 'Could not open this press release.');
      }
    },
    [toast],
  );

  const rowActions = useCallback(
    (record: PressReleaseItem): MenuItem[] => [
      {
        key: `publish-${record.id}`,
        label: 'Publish',
        icon: <Send className="h-4 w-4" aria-hidden="true" />,
        permission: 'press-releases.publish',
        disabledReason: pressReleaseLock(record),
        onClick: () => setPending({ kind: 'publish', record }),
      },
      /*
        Edit is a row ENTRY rather than `onEdit` — the shell's `onEdit` takes no disabled reason, and
        the server refuses changing a published or deleted release: the guard owes its sentence.
      */
      {
        key: `edit-${record.id}`,
        label: 'Edit',
        icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
        permission: 'press-releases.update',
        disabledReason: pressReleaseLock(record),
        onClick: () => void openEdit(record),
      },
      {
        key: `restore-${record.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'press-releases.delete',
        disabledReason: record.isActive ? 'This press release is not deleted.' : null,
        onClick: () => setPending({ kind: 'restore', record }),
      },
    ],
    [openEdit],
  );

  async function runPending() {
    if (!pending) return;

    setRunning(true);
    setError(null);

    try {
      if (pending.kind === 'delete') await pressReleasesApi.remove(pending.record.id);
      else if (pending.kind === 'restore') await pressReleasesApi.restore(pending.record.id);
      else await pressReleasesApi.publish(pending.record.id);

      toast.success(
        `${pending.kind === 'delete' ? 'Deleted' : pending.kind === 'restore' ? 'Restored' : 'Published'} "${pending.record.title}".`,
      );
      setPending(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<PressReleaseItem>
        module="press-releases"
        breadcrumb={['Public Relations', 'Press Release']}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'releaseStatus', label: 'Release status' },
        ]}
        auditEntityTypes={['PressRelease', 'PressReleaseAttachment']}
        rowKey={(record) => record.id}
        fetch={fetchReleases}
        reloadKey={reloadKey}
        searchPlaceholder="Search titles and content"
        emptyMessage="No press releases yet."
        onCreate={() => setCreating(true)}
        createPermission="press-releases.create"
        onRowOpen={(record) => navigate(pressReleasePath(record.id))}
        openPermission="press-releases.read"
        rowActions={rowActions}
        onDelete={(record) => setPending({ kind: 'delete', record })}
        deletePermission="press-releases.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(record) => (record.isActive ? null : 'This press release is already deleted.')}
        otherInfo={(record) => record}
      />

      {creating || editing !== null ? (
        <PressReleaseModal
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(saved) => {
            const created = editing === null;

            setCreating(false);
            setEditing(null);

            if (created) navigate(pressReleasePath(saved.id));
            else refresh();
          }}
        />
      ) : null}

      {pending ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setError(null);
          }}
          onConfirm={() => void runPending()}
          title={pending.kind === 'delete' ? 'Delete press release' : pending.kind === 'restore' ? 'Restore press release' : 'Publish press release'}
          danger={pending.kind === 'delete'}
          requireConfirm={pending.kind !== 'restore'}
          requireCredential={pending.kind === 'delete'}
          loading={running}
          id={`press-release-${pending.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {pending.kind === 'delete'
                  ? `"${pending.record.title}" leaves the register. Nothing is erased — its content and files are kept, and it can be restored.`
                  : pending.kind === 'restore'
                    ? `"${pending.record.title}" returns to the register, with its files.`
                    : `"${pending.record.title}" is published. After this it can no longer be edited, and no file can be added or removed.`}
              </p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<PressReleaseItem>[] = [
  {
    key: 'title',
    header: 'Title',
    width: 420,
    flex: true,
    sortable: true,
    render: (record) => <TextCell value={record.title} />,
  },
  {
    key: 'releaseDate',
    header: 'Release date',
    width: 150,
    sortable: true,
    summary: true,
    // A CALENDAR date — `formatDateOnly`, never the instant path `<DateCell>` takes.
    render: (record) => <TextCell value={formatDateOnly(record.releaseDate)} mono />,
  },
  {
    key: 'attachmentCount',
    header: 'Files',
    width: 90,
    align: 'right',
    summary: true,
    render: (record) => <TextCell value={String(record.attachmentCount)} mono numeric />,
  },
  {
    key: 'releaseStatus',
    header: 'Release status',
    width: 140,
    stage: true,
    render: (record) => <StatusText tone="inactive">{record.statusLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (record) =>
      record.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];

